import { useCallback, useMemo, useState } from 'react';
import type { Connection } from 'app/types/connection.types';
import type { InsurerProduct } from 'app/types/insurer.types';

type SelectorItem = {
  element: string;
  value: string;
};

type StepState = {
  state: Record<string, unknown>;
  form: Record<string, unknown>;
};

type UseRelatedProductsParams = {
  connection: Connection | null;
  products: InsurerProduct[];
  selectedProduct: InsurerProduct | null;
  stepState: StepState;
};

type UseRelatedProductsResult = {
  selectedProductIds: string[];
  selectorItems: SelectorItem[];
  handleChange: (event: { value: unknown }) => void;
  handleProductChange: (newProduct: InsurerProduct | null) => void;
  clearSelection: () => void;
  resolveSubmitValues: (selectedIds: string[]) => { productTypes: string[]; productsIds: string[] };
};

const resolveInitialProductIds = ({
  connection,
  products,
}: Pick<UseRelatedProductsParams, 'connection' | 'products'>): string[] => {
  if (connection?.relatedProductIds && connection.relatedProductIds.length > 0) {
    return connection.relatedProductIds;
  }

  if (connection?.productTypes && connection.productTypes.length > 0) {
    const productIds = products
      .filter(product => (product.productType || []).some(type => connection.productTypes.includes(type)))
      .map(product => product.productId)
      .filter((id, index, arr) => arr.indexOf(id) === index);

    if (productIds.length > 0) {
      return productIds;
    }
  }

  return [];
};

const useRelatedProducts = ({
  connection,
  products,
  selectedProduct,
  stepState,
}: UseRelatedProductsParams): UseRelatedProductsResult => {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>(
    () => (stepState.state.productTypes as string[] | undefined) ?? resolveInitialProductIds({ connection, products }),
  );

  const selectorItems = useMemo<SelectorItem[]>(() => {
    return products
      .filter(product => product['@id'] !== selectedProduct?.['@id'])
      .map(product => ({
        element: product.productName,
        value: product.productId,
      }));
  }, [products, selectedProduct]);

  const handleChange = useCallback(({ value: productTypes }: { value: unknown }) => {
    const values = Array.isArray(productTypes)
      ? productTypes.map((item: unknown) =>
          typeof item === 'object' && item !== null && 'value' in item ? (item as { value: string }).value : item,
        )
      : [];
    setSelectedProductIds(values as string[]);
  }, []);

  const handleProductChange = useCallback((newProduct: InsurerProduct | null) => {
    if (newProduct?.['@id']) {
      setSelectedProductIds(prevIds => prevIds.filter(id => id !== newProduct.productId));
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedProductIds([]);
  }, []);

  const resolveSubmitValues = useCallback(
    (selectedIds: string[]) => {
      const productTypeStrings = Array.from(
        new Set(
          selectedIds.flatMap(selectedProductId => {
            const product = products.find(p => p.productId === selectedProductId);
            return product?.productType || [];
          }),
        ),
      );

      return {
        productTypes: productTypeStrings,
        productsIds: selectedIds,
      };
    },
    [products],
  );

  return {
    selectedProductIds,
    selectorItems,
    handleChange,
    handleProductChange,
    clearSelection,
    resolveSubmitValues,
  };
};

export default useRelatedProducts;
